/**
 * AuditTrail Component
 *
 * TurboTax-style audit trail showing all system decisions and user actions.
 * Provides transparency and explainability for compliance workflows.
 *
 * Features:
 * - Timeline view of all events
 * - Filter by event type (system decisions, user actions, overrides)
 * - Rule attribution for system decisions
 * - Override tracking with user reasons
 * - Confidence level display
 * - Export and print functionality
 *
 * Part of Phase 3: Submission Guidance & Review Tracking
 */

import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Bot,
  User,
  FileText,
  AlertTriangle,
  CheckCircle2,
  Upload,
  Edit3,
  Shield,
  Send,
  ArrowRight,
  Search,
  Download,
  Printer,
  Filter,
  Clock,
  Info,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDistanceToNow, format } from 'date-fns';
import type {
  AuditEntry,
  AuditEntryType,
  ConfidenceLevel,
  SystemDecisionEntry,
  UserActionEntry,
  UserOverrideEntry,
  DocumentEventEntry,
  StatusChangeEntry,
  ComplianceCheckEntry,
  SubmissionEventEntry,
} from '@/types/jurisdiction';

interface AuditTrailProps {
  entries: AuditEntry[];
  className?: string;
  maxHeight?: string;
  showFilters?: boolean;
  showExport?: boolean;
  title?: string;
}

type FilterType = 'all' | 'system' | 'user' | 'overrides' | 'documents';

function getEntryIcon(entry: AuditEntry) {
  switch (entry.type) {
    case 'system_decision':
      return <Bot className="h-4 w-4" />;
    case 'user_action':
      return <Edit3 className="h-4 w-4" />;
    case 'user_override':
      return <AlertTriangle className="h-4 w-4" />;
    case 'document_event':
      return <FileText className="h-4 w-4" />;
    case 'status_change':
      return <ArrowRight className="h-4 w-4" />;
    case 'compliance_check':
      return <Shield className="h-4 w-4" />;
    case 'submission_event':
      return <Send className="h-4 w-4" />;
    default:
      return <Info className="h-4 w-4" />;
  }
}

function getEntryColor(entry: AuditEntry): string {
  switch (entry.type) {
    case 'system_decision':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
    case 'user_action':
      return 'bg-slate-100 text-slate-700 dark:bg-slate-800/50 dark:text-slate-300';
    case 'user_override':
      return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
    case 'document_event':
      return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300';
    case 'status_change':
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
    case 'compliance_check':
      return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300';
    case 'submission_event':
      return 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300';
    default:
      return 'bg-gray-100 text-gray-700 dark:bg-gray-800/50 dark:text-gray-300';
  }
}

function ConfidenceBadge({ confidence }: { confidence: ConfidenceLevel }) {
  const colors = {
    high: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800',
    medium: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800',
    low: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800',
  };

  return (
    <Badge variant="outline" className={cn('text-xs', colors[confidence])}>
      {confidence.toUpperCase()} confidence
    </Badge>
  );
}

function SystemDecisionContent({ entry }: { entry: SystemDecisionEntry }) {
  const { decision } = entry;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-medium">{decision.action}</span>
        <ConfidenceBadge confidence={decision.confidence} />
      </div>
      <p className="text-sm text-muted-foreground">{decision.result}</p>
      {decision.source && (
        <p className="text-xs text-muted-foreground">
          Source: {decision.source}
        </p>
      )}
      {decision.rules.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {decision.rules.map((rule, i) => (
            <Badge key={i} variant="secondary" className="text-xs font-normal">
              {rule}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

function UserActionContent({ entry }: { entry: UserActionEntry }) {
  const { action } = entry;

  return (
    <div className="space-y-1">
      <p className="font-medium">{action.description}</p>
      {action.field && action.previousValue !== undefined && (
        <p className="text-sm text-muted-foreground">
          <span className="line-through">{action.previousValue || '(empty)'}</span>
          {' → '}
          <span className="font-medium">{action.newValue || '(empty)'}</span>
        </p>
      )}
    </div>
  );
}

function UserOverrideContent({ entry }: { entry: UserOverrideEntry }) {
  const { override } = entry;

  return (
    <div className="space-y-2 p-2 bg-amber-50 dark:bg-amber-950/20 rounded-md border border-amber-200 dark:border-amber-800">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <span className="font-medium text-amber-800 dark:text-amber-200">
          Override System Recommendation
        </span>
      </div>
      <div className="text-sm space-y-1">
        <p>
          <span className="text-muted-foreground">System said:</span>{' '}
          {override.systemRecommendation}
        </p>
        <p>
          <span className="text-muted-foreground">User decided:</span>{' '}
          {override.userDecision}
        </p>
        <p>
          <span className="text-muted-foreground">Reason:</span>{' '}
          <em>"{override.reason}"</em>
        </p>
      </div>
      <ConfidenceBadge confidence={override.resultingConfidence} />
    </div>
  );
}

function DocumentEventContent({ entry }: { entry: DocumentEventEntry }) {
  const { document } = entry;

  const actionText = {
    uploaded: 'Uploaded',
    matched: 'Matched to requirement',
    rejected: 'Rejected',
    deleted: 'Deleted',
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <Upload className="h-3 w-3" />
        <span className="font-medium">
          {actionText[document.action]}: {document.fileName}
        </span>
      </div>
      {document.satisfiesRequirement && (
        <p className="text-sm text-muted-foreground flex items-center gap-1">
          <CheckCircle2 className="h-3 w-3 text-green-500" />
          Satisfies: "{document.satisfiesRequirement}"
        </p>
      )}
      {document.autoMatched && (
        <Badge variant="secondary" className="text-xs">
          Auto-matched by system
        </Badge>
      )}
    </div>
  );
}

function StatusChangeContent({ entry }: { entry: StatusChangeEntry }) {
  const { statusChange } = entry;

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="font-normal">
          {statusChange.previousStatus}
        </Badge>
        <ArrowRight className="h-3 w-3 text-muted-foreground" />
        <Badge variant="default">{statusChange.newStatus}</Badge>
      </div>
      {statusChange.trigger && (
        <p className="text-xs text-muted-foreground">
          Trigger: {statusChange.trigger}
        </p>
      )}
    </div>
  );
}

function ComplianceCheckContent({ entry }: { entry: ComplianceCheckEntry }) {
  const { check } = entry;

  const statusColors = {
    pass: 'text-green-600 dark:text-green-400',
    fail: 'text-red-600 dark:text-red-400',
    needs_review: 'text-amber-600 dark:text-amber-400',
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Shield className="h-4 w-4" />
        <span className={cn('font-medium', statusColors[check.status])}>
          Compliance Check: {check.status.replace('_', ' ').toUpperCase()}
        </span>
        <Badge variant="outline" className="font-mono">
          {check.score}%
        </Badge>
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <span className="text-muted-foreground">Rules checked:</span>{' '}
          {check.rulesChecked}
        </div>
        <div>
          <span className="text-muted-foreground">Passed:</span>{' '}
          {check.rulesPassed}
        </div>
        {check.criticalFindings > 0 && (
          <div className="text-red-600 dark:text-red-400">
            Critical findings: {check.criticalFindings}
          </div>
        )}
        {check.warningFindings > 0 && (
          <div className="text-amber-600 dark:text-amber-400">
            Warnings: {check.warningFindings}
          </div>
        )}
      </div>
    </div>
  );
}

function SubmissionEventContent({ entry }: { entry: SubmissionEventEntry }) {
  const { submission } = entry;

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <Send className="h-4 w-4" />
        <span className="font-medium">
          Submitted to {submission.jurisdictionName} via {submission.method.replace('_', ' ')}
        </span>
      </div>
      {submission.confirmationNumber && (
        <p className="text-sm">
          Confirmation: <code className="font-mono">{submission.confirmationNumber}</code>
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        {submission.submittedDocuments.length} documents submitted
      </p>
    </div>
  );
}

function AuditEntryContent({ entry }: { entry: AuditEntry }) {
  switch (entry.type) {
    case 'system_decision':
      return <SystemDecisionContent entry={entry} />;
    case 'user_action':
      return <UserActionContent entry={entry} />;
    case 'user_override':
      return <UserOverrideContent entry={entry} />;
    case 'document_event':
      return <DocumentEventContent entry={entry} />;
    case 'status_change':
      return <StatusChangeContent entry={entry} />;
    case 'compliance_check':
      return <ComplianceCheckContent entry={entry} />;
    case 'submission_event':
      return <SubmissionEventContent entry={entry} />;
    default:
      return <p className="text-sm text-muted-foreground">Unknown entry type</p>;
  }
}

function AuditEntryRow({ entry }: { entry: AuditEntry }) {
  const entryDate = new Date(entry.timestamp);
  const icon = getEntryIcon(entry);
  const colorClass = getEntryColor(entry);

  return (
    <div className="flex gap-3 py-3">
      {/* Timeline indicator */}
      <div className="flex flex-col items-center">
        <div
          className={cn(
            'flex items-center justify-center w-8 h-8 rounded-full',
            colorClass
          )}
        >
          {icon}
        </div>
        <div className="w-px flex-1 bg-border mt-2" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pb-2">
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center gap-1 cursor-help">
                  <Clock className="h-3 w-3" />
                  {formatDistanceToNow(entryDate, { addSuffix: true })}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {format(entryDate, 'PPpp')}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <span>•</span>
          <span className="flex items-center gap-1">
            {entry.actorType === 'system' ? (
              <>
                <Bot className="h-3 w-3" />
                System
              </>
            ) : (
              <>
                <User className="h-3 w-3" />
                {entry.actorName || 'User'}
              </>
            )}
          </span>
        </div>
        <AuditEntryContent entry={entry} />
      </div>
    </div>
  );
}

export function AuditTrail({
  entries,
  className,
  maxHeight = '500px',
  showFilters = true,
  showExport = true,
  title = 'Audit & Decision Trail',
}: AuditTrailProps) {
  const [filter, setFilter] = useState<FilterType>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredEntries = useMemo(() => {
    let filtered = [...entries];

    // Apply type filter
    switch (filter) {
      case 'system':
        filtered = filtered.filter(
          (e) => e.actorType === 'system' || e.type === 'system_decision' || e.type === 'compliance_check'
        );
        break;
      case 'user':
        filtered = filtered.filter(
          (e) => e.actorType === 'user' && e.type !== 'user_override'
        );
        break;
      case 'overrides':
        filtered = filtered.filter((e) => e.type === 'user_override');
        break;
      case 'documents':
        filtered = filtered.filter((e) => e.type === 'document_event');
        break;
    }

    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((entry) => {
        // Search in entry content based on type
        const searchableText = JSON.stringify(entry).toLowerCase();
        return searchableText.includes(query);
      });
    }

    // Sort by timestamp descending (most recent first)
    return filtered.sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }, [entries, filter, searchQuery]);

  const handleExport = () => {
    const data = filteredEntries.map((entry) => ({
      timestamp: entry.timestamp,
      type: entry.type,
      actor: entry.actorType === 'system' ? 'System' : entry.actorName || 'User',
      details: JSON.stringify(entry),
    }));
    const csv = [
      'Timestamp,Type,Actor,Details',
      ...data.map((row) => `${row.timestamp},${row.type},${row.actor},"${row.details.replace(/"/g, '""')}"`),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-trail-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    window.print();
  };

  if (entries.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="text-lg">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-muted-foreground py-8">
            <Shield className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No audit entries yet.</p>
            <p className="text-sm">Actions and decisions will be logged here.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-lg flex items-center gap-2">
            {title}
            <Badge variant="secondary">{entries.length} entries</Badge>
          </CardTitle>
          {showExport && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleExport}>
                <Download className="h-4 w-4 mr-1" />
                Export
              </Button>
              <Button variant="outline" size="sm" onClick={handlePrint}>
                <Printer className="h-4 w-4 mr-1" />
                Print
              </Button>
            </div>
          )}
        </div>

        {showFilters && (
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <Select value={filter} onValueChange={(v) => setFilter(v as FilterType)}>
              <SelectTrigger className="w-[160px] h-8">
                <Filter className="h-3 w-3 mr-1" />
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Events</SelectItem>
                <SelectItem value="system">System Decisions</SelectItem>
                <SelectItem value="user">User Actions</SelectItem>
                <SelectItem value="overrides">Overrides Only</SelectItem>
                <SelectItem value="documents">Documents</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
              <Input
                placeholder="Search audit trail..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-8 pl-7"
              />
            </div>
          </div>
        )}
      </CardHeader>

      <CardContent className="pt-0">
        <Separator className="mb-3" />

        <ScrollArea style={{ maxHeight }} className="pr-4">
          {filteredEntries.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No entries match your filters.</p>
            </div>
          ) : (
            <div className="space-y-0">
              {filteredEntries.map((entry) => (
                <AuditEntryRow key={entry.id} entry={entry} />
              ))}
            </div>
          )}
        </ScrollArea>

        <Separator className="mt-3" />
        <div className="flex items-center justify-between pt-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <Bot className="h-3 w-3" /> System Decision
            </span>
            <span className="flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> User Override
            </span>
            <span className="flex items-center gap-1">
              <FileText className="h-3 w-3" /> Document
            </span>
            <span className="flex items-center gap-1">
              <Edit3 className="h-3 w-3" /> Edit
            </span>
          </div>
          <span>
            Showing {filteredEntries.length} of {entries.length} entries
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

export default AuditTrail;
